/**
 * Client quest model — an optimistic MIRROR of the server-authoritative
 * quest state (the server owns progress; this is display + intent). Mutations
 * arrive as a whole `QuestStateMsg` push and REPLACE the local state, bumping
 * `rev` (the same rev-counter pattern `PlayerRpg` uses: the journal panel and
 * the HUD tracker re-render off that single counter).
 *
 * Quest DEFINITIONS never travel the wire — they're re-derived locally and
 * deterministically from the self-describing id: campaign ids (`camp:*`) hit
 * the authored registry; board ids (`${town.seed}:q:${epoch}:${i}`) re-derive
 * against the CURRENT system def. Resolved defs are cached by id so a journal
 * entry keeps its title even after the player warps out of the town's system.
 */
import {
  campaignQuest,
  parseQuestId,
  questReady,
  resolveBoardQuest,
  type ObjectiveDef,
  type QuestDef,
  type QuestInstance,
  type QuestStateMsg,
  type SystemDef,
  systemDefFor,
} from "@hyperlight/shared";

/** Short verb per objective kind for the progress toasts ("BOUNTY 2/5"). */
const KIND_LABEL: Record<ObjectiveDef["kind"], string> = {
  kill: "BOUNTY",
  collect: "COLLECTED",
  talk: "SPOKE",
  deliver: "DELIVERED",
  goto: "REACHED",
};

/** The system id a town/settlement seed embeds (`hyperlight:sys:x:y:z:planet:…`
 *  → "x:y:z"; C4 home seeds → "home:<uid>"), or null when it isn't one of
 *  ours. Mirrors the server's systemIdFromTownSeed. */
function seedSystemId(seed: string): string | null {
  const m = /^hyperlight:(?:sys:(-?\d{1,4}:-?\d{1,4}:-?\d{1,4})|(home:[A-Za-z0-9_-]{1,64})):planet:/.exec(seed);
  return m ? (m[1] ?? m[2]) : null;
}

export interface QuestLogHandlers {
  /** Progress / stage / completion toasts. */
  toast: (line: string) => void;
  /** The current system def, for re-deriving board quest definitions. */
  systemDef: () => SystemDef;
}

export class QuestLog {
  /** Active instances (including ones ready to turn in). */
  act: QuestInstance[] = [];
  /** Recently-completed quest ids (server-capped). */
  done: string[] = [];
  /** Bumped on every state replace — cheap dirty flag for panel + HUD. */
  rev = 0;
  /** Monotonic intent seq (stamped by the main-loop sender). */
  seq = 0;
  /** Last state-push seq the server sent us. */
  ack = 0;
  /** Quest id whose objective the HUD tracker + world marker follow. */
  tracked: string | null = null;

  /** Cache resolved defs by id so titles survive leaving the system. */
  private defCache = new Map<string, QuestDef>();

  /** False until the first server push (the join/reconnect seed) is adopted —
   *  that seed is the whole current state, not a set of fresh mutations, so it
   *  must NOT fire accept/complete toasts or hijack the player's tracked quest. */
  private hydrated = false;

  constructor(private handlers: QuestLogHandlers) {}

  /** Re-derive a quest def from its id (campaign registry or board re-gen),
   *  cached. Board ids embed their epoch, so the id alone is self-contained. */
  resolveDef(q: string): QuestDef | null {
    const cached = this.defCache.get(q);
    if (cached) return cached;
    let def: QuestDef | null = null;
    if (q.startsWith("camp:")) {
      def = campaignQuest(q);
    } else {
      const parsed = parseQuestId(q);
      if (parsed) def = resolveBoardQuest(this.systemForTownSeed(parsed.townSeed), q, parsed.ep);
    }
    if (def) this.defCache.set(q, def);
    return def;
  }

  /**
   * The system a board quest belongs to, derived from its OWN town seed (the id
   * is self-describing) so an active quest whose home town is in a DIFFERENT
   * system still resolves — mirrors the server's systemIdFromTownSeed +
   * systemDef. Falls back to the player's current system only if the coords
   * can't be parsed. The id-keyed defCache means this runs at most once per id.
   */
  private systemForTownSeed(townSeed: string): SystemDef {
    const id = seedSystemId(townSeed);
    const def = id ? systemDefFor(id) : null;
    return def ?? this.handlers.systemDef();
  }

  defOf(inst: QuestInstance): QuestDef | null {
    return this.resolveDef(inst.q);
  }

  instById(id: string): QuestInstance | null {
    return this.act.find((i) => i.id === id) ?? null;
  }

  hasActive(q: string): boolean {
    return this.act.some((i) => i.q === q);
  }

  isDone(q: string): boolean {
    return this.done.includes(q);
  }

  ready(inst: QuestInstance): boolean {
    const def = this.defOf(inst);
    return def ? questReady(def, inst) : false;
  }

  /** The current (first incomplete) objective of an instance's active stage. */
  currentObjective(inst: QuestInstance): { obj: ObjectiveDef; oi: number } | null {
    const def = this.defOf(inst);
    if (!def) return null;
    const stage = def.stages[inst.st];
    if (!stage) return null; // ready to turn in
    for (let oi = 0; oi < stage.objectives.length; oi++) {
      if ((inst.prog[oi] ?? 0) < stage.objectives[oi].n) return { obj: stage.objectives[oi], oi };
    }
    return { obj: stage.objectives[0], oi: 0 };
  }

  /**
   * The home town seed a quest anchors to: for board quests it's parsed out
   * of the id; for the campaign it's derived from a resolved target (an npc
   * id is `${townSeed}:npc:i`; a goto target IS the town seed). Null if the
   * quest has no town anchor at all.
   */
  questTownSeed(inst: QuestInstance): string | null {
    const parsed = parseQuestId(inst.q);
    if (parsed) return parsed.townSeed;
    for (const stage of inst.tg) {
      for (const t of stage) {
        if (!t) continue;
        const npcAt = t.indexOf(":npc:");
        if (npcAt >= 0) return t.slice(0, npcAt);
        if (t.includes(":town:")) return t; // a goto target town seed
      }
    }
    return null;
  }

  /** First resolved npc-id target across an instance's stages — the campaign
   *  "giver" for a quest that names no explicit giver id. */
  firstNpcTarget(inst: QuestInstance): string | null {
    for (const stage of inst.tg) for (const t of stage) if (t && (t.includes(":npc:") || t.includes(":canon:"))) return t;
    return null;
  }

  /**
   * Where a quest's current objective physically lives: the system id plus
   * (when a target embeds one) the settlement id. Board ids, npc targets and
   * canon targets are all self-describing; a canon id names only the owner's
   * home system — the caller resolves the capital. Null when the quest anchors
   * to nothing physical (e.g. a pure kill/collect def with an opaque id).
   */
  questAnchor(inst: QuestInstance): { systemId: string; settlementId: string | null } | null {
    const cur = this.currentObjective(inst);
    const target = this.ready(inst)
      ? this.firstNpcTarget(inst)
      : cur ? (inst.tg[inst.st]?.[cur.oi] ?? null) : null;
    if (target) {
      const canon = /^home:([A-Za-z0-9_-]{1,64}):canon:/.exec(target);
      if (canon) return { systemId: `home:${canon[1]}`, settlementId: null };
      const npcAt = target.indexOf(":npc:");
      if (npcAt >= 0) {
        const settlementId = target.slice(0, npcAt);
        const systemId = seedSystemId(settlementId);
        if (systemId) return { systemId, settlementId };
      }
    }
    const seed = this.questTownSeed(inst);
    if (seed) {
      const systemId = seedSystemId(seed);
      if (systemId) return { systemId, settlementId: seed };
    }
    return null;
  }

  /** The tracked instance, if still active. */
  trackedInst(): QuestInstance | null {
    return this.tracked ? this.instById(this.tracked) : null;
  }

  /** HUD tracker line: "<title>: <objective> k/n" (or a READY banner). */
  trackedLine(): string | null {
    const inst = this.trackedInst();
    if (!inst) return null;
    const def = this.defOf(inst);
    const title = def?.title ?? "QUEST";
    if (this.ready(inst)) return `${title} — READY TO TURN IN`;
    const cur = this.currentObjective(inst);
    if (!cur) return title;
    const n = cur.obj.n;
    const k = inst.prog[cur.oi] ?? 0;
    return n > 1 ? `${title}: ${cur.obj.text} ${k}/${n}` : `${title}: ${cur.obj.text}`;
  }

  /** Toggle tracking of a quest id (null clears). */
  track(id: string | null): void {
    this.tracked = this.tracked === id ? null : id;
    this.rev++;
  }

  /**
   * Adopt a whole server push. Diffs the previous state to surface progress /
   * stage / completion toasts, replaces state, and prunes an invalid track.
   */
  apply(msg: QuestStateMsg): void {
    if (!this.hydrated) {
      // First push after (re)connect is the server's full seed (seq 0 at join),
      // not a batch of mutations. Adopt it SILENTLY: warm the def cache so
      // titles/tracking resolve, but fire no toasts and don't auto-track (the
      // player re-tracks from the journal). Later pushes diff normally.
      this.hydrated = true;
      if (msg.seq > 0) this.ack = msg.seq;
      this.act = msg.act;
      this.done = msg.done;
      for (const inst of this.act) this.resolveDef(inst.q);
      for (const id of this.done) this.resolveDef(id);
      this.rev++;
      return;
    }
    if (msg.seq > 0 && msg.seq < this.ack) return; // stale (out-of-order) push
    if (msg.seq > 0) this.ack = msg.seq;
    const prevAct = new Map(this.act.map((i) => [i.id, i]));
    const prevDone = new Set(this.done);

    for (const ni of msg.act) {
      const old = prevAct.get(ni.id);
      const def = this.resolveDef(ni.q);
      const title = def?.title ?? "QUEST";
      if (!old) {
        // Fresh accept — announce + auto-track so the HUD points at it now.
        this.handlers.toast(`QUEST ACCEPTED — ${title.toUpperCase()}`);
        this.tracked = ni.id;
        continue;
      }
      if (ni.st > old.st) {
        if (def && ni.st >= def.stages.length) {
          const seed = this.questTownSeed(ni);
          const where = seed ? this.townShortName(seed) : null;
          this.handlers.toast(where ? `QUEST COMPLETE — TURN IN AT ${where}` : "QUEST COMPLETE — READY TO TURN IN");
        } else {
          this.handlers.toast("STAGE COMPLETE");
        }
        continue;
      }
      // Same stage: surface the first objective whose count climbed.
      if (def) {
        const stage = def.stages[ni.st];
        if (stage) {
          for (let oi = 0; oi < stage.objectives.length; oi++) {
            const before = old.prog[oi] ?? 0;
            const after = ni.prog[oi] ?? 0;
            if (after > before) {
              const obj = stage.objectives[oi];
              this.handlers.toast(`${KIND_LABEL[obj.kind]} ${after}/${obj.n}`);
              break;
            }
          }
        }
      }
    }

    // Newly-completed quests: reward toast + auto-untrack.
    for (const id of msg.done) {
      if (prevDone.has(id)) continue;
      const def = this.resolveDef(id);
      const reward = def ? this.rewardLine(def) : "";
      this.handlers.toast(`QUEST COMPLETE${def ? ` — ${def.title.toUpperCase()}` : ""}${reward ? ` · ${reward}` : ""}`);
      if (this.tracked === id) this.tracked = null;
    }

    this.act = msg.act;
    this.done = msg.done;
    // Drop a track that's no longer active (abandoned/turned in without a
    // done entry we saw) — the user can re-track from the journal.
    if (this.tracked && !this.instById(this.tracked)) this.tracked = null;
    this.rev++;
  }

  /** A compact reward summary ("+400 CR · Medkit"). */
  rewardLine(def: QuestDef): string {
    const parts: string[] = [];
    if (def.reward.credits) parts.push(`+${def.reward.credits} CR`);
    for (const it of def.reward.items ?? []) parts.push(`${it.id}${it.n > 1 ? ` ×${it.n}` : ""}`);
    return parts.join(" · ");
  }

  /** The last segment of a town seed, upper-cased ("...:town:3" → "TOWN 3").
   *  Board quest ids don't carry the pretty name, so this is a stable stand-in
   *  when a def isn't resolvable; callers with a def should prefer its title. */
  private townShortName(townSeed: string): string {
    const m = townSeed.match(/:town:(\d+)$/);
    return m ? `SETTLEMENT ${m[1]}` : "THE BOARD";
  }
}
